import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { EmptyState } from '@vnetwork/ui/data';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Input,
  Textarea,
  Checkbox,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useWaapEnabledSites,
  useCreateRateLimitRule,
  useRateLimitRule,
  useUpdateRateLimitRule,
  emptyRateLimitRule,
  PBR_PATH_CONDITIONS,
  PBR_RATE_LIMIT_KEYS,
  PBR_DEVICE_TYPES,
  PBR_AUTO_LIST_KEYS,
  PBR_PROBABILITIES,
  PBR_RATE_MEASUREMENTS,
  PBR_TIME_WINDOW_UNITS,
  type RateLimitRuleInput,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './RateLimitingRuleForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const PATH_MAX = 50;

/**
 * Add a Programmable Mitigation → Rate Limiting rule. Account-level: the rule is
 * created once and applied to one or more websites ("Apply to"), via the PUBLIC
 * Open-API surface. Sibling to the Web Access Control form, with a Rate
 * Measurement section (QPS rate + burst, or a max-request count per window unit).
 * The public PBR DTO carries no Filters or Action Set, so those sections are
 * omitted (see src/api/waap/pbr.ts).
 */
export function RateLimitingRuleForm() {
  const navigate = useNavigate();
  const { serviceId, ruleId } = useParams<{ serviceId: string; ruleId: string }>();
  const isEdit = !!ruleId;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;
  const listHref = `${base}/programmable-mitigation`;

  const { selected } = useWaapWebsite();
  const domainId = selected?.domainId;
  const { data: sites = [] } = useWaapEnabledSites(domainId);

  const [form, setForm] = useState<RateLimitRuleInput>(emptyRateLimitRule);

  // Edit mode: seed the form from the rule's detail once it loads.
  const { data: detail } = useRateLimitRule(domainId, ruleId);
  useEffect(() => {
    if (!detail) return;
    setForm({
      name: detail.name,
      description: detail.description,
      path_operator: detail.path_operator,
      path_pattern: detail.path_pattern,
      key_type: detail.key_type,
      rate_limit_type: detail.rate_limit_type || 'rate',
      rate: detail.rate,
      burst: detail.burst,
      window_unit: detail.window_unit || 'minute',
      max_requests: detail.max_requests,
      dev_type: detail.dev_type ?? [],
      triggering_probability: detail.triggering_probability,
      auto_list_key: detail.auto_list_key,
      auto_list_duration: detail.auto_list_duration,
      websites: detail.websites ?? [],
    });
  }, [detail]);

  const set = <K extends keyof RateLimitRuleInput>(key: K, value: RateLimitRuleInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Rate measurement toggle — QPS zeroes the window count, Time Window zeroes
  // the rate + burst.
  const setMeasurement = (v: string) =>
    setForm((f) => ({
      ...f,
      rate_limit_type: v,
      ...(v === 'rate' ? { max_requests: 0 } : { rate: 0, burst: 0 }),
    }));
  const isQps = form.rate_limit_type === 'rate';

  // Device type toggles.
  const devSet = useMemo(() => new Set(form.dev_type), [form.dev_type]);
  const toggleDev = (v: string) =>
    set('dev_type', devSet.has(v) ? form.dev_type.filter((x) => x !== v) : [...form.dev_type, v]);

  // Apply-to toggles.
  const siteSet = useMemo(() => new Set(form.websites), [form.websites]);
  const toggleSite = (id: number) =>
    set('websites', siteSet.has(id) ? form.websites.filter((x) => x !== id) : [...form.websites, id]);

  const create = useCreateRateLimitRule(domainId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'pbr_rate_limit_rule', 'create');
      show({ tone: 'success', title: 'Rate limiting rule created' });
      navigate(listHref);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateRateLimitRule(domainId ?? '', ruleId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'pbr_rate_limit_rule', 'edit');
      show({ tone: 'success', title: 'Rate limiting rule updated' });
      navigate(listHref);
    },
    onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const canSubmit =
    nameValid &&
    !!form.path_pattern.trim() &&
    (isQps ? form.rate > 0 : form.max_requests > 0) &&
    form.websites.length > 0 &&
    !!domainId &&
    !saving;

  const submit = () => {
    const payload = {
      ...form,
      name: form.name.trim(),
      description: form.description.trim(),
      path_pattern: form.path_pattern.trim(),
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'Programmable Mitigation', slug: 'programmable-mitigation' },
              { label: 'Rate Limiting Rule' },
            ]}
          />
        }
        title="Rate Limiting Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
        {/* General */}
        <Card flat>
          <CardHeader>
            <CardTitle>General</CardTitle>
          </CardHeader>
          <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* Triggering Condition */}
        <Card flat>
          <CardHeader>
            <CardTitle>Triggering Condition</CardTitle>
            <CardDescription>The request must match all conditions below.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.fieldGrid}>
              <Field label="Path" required description="Configure the path targeted by this rule.">
                {() => (
                  <div className={styles.row2}>
                    <Select
                      value={form.path_operator}
                      onValueChange={(v) => set('path_operator', v)}
                    >
                      <SelectTrigger className={styles.select150}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PBR_PATH_CONDITIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      className={styles.flex1}
                      value={form.path_pattern}
                      maxLength={PATH_MAX}
                      placeholder="/"
                      onChange={(e) => set('path_pattern', e.target.value)}
                    />
                  </div>
                )}
              </Field>

              <Field label="Key" required description="The request property used to count and limit traffic: IP address, browser fingerprint, or Cookie ID.">
                {() => (
                  <Select value={form.key_type} onValueChange={(v) => set('key_type', v)}>
                    <SelectTrigger className={styles.selectMax300}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PBR_RATE_LIMIT_KEYS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* Rate Measurement */}
        <Card flat>
          <CardHeader>
            <CardTitle>Rate Measurement</CardTitle>
            <CardDescription>Limit either a per-second query rate (with a burst allowance) or a maximum number of requests per time-window unit.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.fieldGrid}>
              <SegmentedControl
                items={PBR_RATE_MEASUREMENTS}
                value={form.rate_limit_type}
                onValueChange={setMeasurement}
                aria-label="Rate measurement"
              />

              {isQps ? (
                <div className={styles.measureRow}>
                  <Field label="Queries per second" required>
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={1}
                        value={form.rate}
                        className={styles.input200}
                        onChange={(e) => set('rate', Number(e.target.value))}
                      />
                    )}
                  </Field>
                  <Field label="Burst" description="Extra requests allowed above the QPS rate in a short spike. Set to 0 to enforce the rate strictly.">
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={0}
                        value={form.burst}
                        className={styles.input200}
                        onChange={(e) => set('burst', Number(e.target.value))}
                      />
                    )}
                  </Field>
                </div>
              ) : (
                <div className={styles.autoRow}>
                  <Field label="Max requests" required>
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={1}
                        value={form.max_requests}
                        className={styles.input180}
                        onChange={(e) => set('max_requests', Number(e.target.value))}
                      />
                    )}
                  </Field>
                  <Field label="Per" required>
                    {() => (
                      <Select value={form.window_unit} onValueChange={(v) => set('window_unit', v)}>
                        <SelectTrigger className={styles.select140}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PBR_TIME_WINDOW_UNITS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Device Type */}
        <Card flat>
          <CardHeader>
            <CardTitle>Device Type</CardTitle>
            <CardDescription>The rule applies to requests from the selected device types.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.deviceRow}>
              {PBR_DEVICE_TYPES.map((d) => (
                <label key={d.value} className={styles.checkLabel}>
                  <Checkbox checked={devSet.has(d.value)} onCheckedChange={() => toggleDev(d.value)} />
                  <span>{d.label}</span>
                </label>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Dynamic Action */}
        <Card flat>
          <CardHeader>
            <CardTitle>Dynamic Action</CardTitle>
            <CardDescription>An advanced protection strategy designed to prevent attackers from predicting the security policy by repetitive attacks.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.fieldGrid}>
              <Field label="Triggering Probability" required description="Configure the triggering probability of the action.">
                {() => (
                  <Select
                    value={String(form.triggering_probability)}
                    onValueChange={(v) => set('triggering_probability', Number(v))}
                  >
                    <SelectTrigger className={styles.selectMax160}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PBR_PROBABILITIES.map((p) => (
                        <SelectItem key={p} value={String(p)}>
                          {p}%
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* Auto-Web ACL */}
        <Card flat>
          <CardHeader>
            <CardTitle>Auto-Web ACL</CardTitle>
            <CardDescription>Auto-web ACL automatically controls passing or rejecting requests. Configure the key type and how long values live in the auto-web ACL.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.autoRow}>
              <Field label="Key" description="The request property (IP or Fingerprint) used to identify entries in the auto-web ACL list.">
                {() => (
                  <Select value={form.auto_list_key} onValueChange={(v) => set('auto_list_key', v)}>
                    <SelectTrigger className={styles.select160}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PBR_AUTO_LIST_KEYS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Duration (seconds)" description="How long the entry stays in the auto-web ACL before expiring.">
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min={0}
                    value={form.auto_list_duration}
                    className={styles.input200}
                    onChange={(e) => set('auto_list_duration', Number(e.target.value))}
                  />
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* Apply to */}
        <Card flat>
          <CardHeader>
            <CardTitle>Apply to</CardTitle>
            <CardDescription>Select the websites this rule applies to.</CardDescription>
          </CardHeader>
          <CardBody>
            {sites.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No websites available" description="Enable WAAP protection on a website first, then apply this rule." />
            ) : (
              <div className={styles.applyGrid}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.checkLabel}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => navigate(listHref)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Container>
  );
}
